/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import { wechatAppId } from 'config';
import * as constants from 'data/constants/session';
import { fetchSingle as fetchProfile } from 'data/actions/profile';

// Generate a cryptographically secure random hex string for browser compatibility
const generateRandomState = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const wechatLogin = () => {
  // Generate a cryptographically secure random state for security
  const state = generateRandomState();

  // Store the state in localStorage for validation
  localStorage.setItem('wechat_oauth_state', state);

  // Construct the WeChat QR code login URL
  const redirectUri = encodeURIComponent('https://my.soundtalks.cn/wechat/callback');
  const wechatLoginUrl = `https://open.weixin.qq.com/connect/qrconnect?appid=${wechatAppId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=${state}#wechat_redirect`;

  // Redirect to WeChat login page
  window.location.href = wechatLoginUrl;
};

export const wechatInviteLogin = (inviteKey) => () => {
  // Generate a random state for security
  const state = generateRandomState();

  // Store the state and invite key in localStorage for validation
  localStorage.setItem('wechat_oauth_state', state);
  localStorage.setItem('wechat_invite_key', inviteKey);

  // Construct the WeChat QR code login URL with invite token parameter
  const redirectUri = encodeURIComponent('https://my.soundtalks.cn/wechat/callback');
  const wechatLoginUrl = `https://open.weixin.qq.com/connect/qrconnect?appid=${wechatAppId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=${state}#wechat_redirect`;

  // Redirect to WeChat login page
  window.location.href = wechatLoginUrl;
};

export const wechatOAuthCallback =
  (code, state, inviteToken) =>
  async (dispatch, getState, { api }) => {
    dispatch({ type: constants.CALLBACK });

    try {
      // Prepare the request payload
      const payload = {
        code,
        state,
      };

      // Check for invite token from URL parameter or localStorage
      let finalInviteToken = inviteToken;
      if (!finalInviteToken) {
        finalInviteToken = localStorage.getItem('wechat_invite_key');
      }

      // Add invite token if available
      if (finalInviteToken) {
        payload.invite_token = finalInviteToken;
        // Clear the stored invite key after use
        localStorage.removeItem('wechat_invite_key');
      }

      // Call the backend WeChat OAuth callback endpoint
      const res = await api.post('/api/v2/wechat/oauth/callback/login/', payload);

      if (res.data.user_exists) {
        api.setAuthorization(res.data.access);
        dispatch({
          type: constants.LOGIN_SUCCESS,
          payload: {
            ...res.data,
          },
        });
      }

      dispatch({
        type: constants.CALLBACK_SUCCESS,
        payload: {
          ...res.data,
        },
      });

      return res.data;
    } catch (e) {
      console.error('WeChat OAuth callback error:', e);

      // Dispatch failure action
      dispatch({ type: constants.CALLBACK_FAILED });

      // Handle different error scenarios
      let errorMessage = 'WeChat authentication failed';
      if (e.response && e.response.data) {
        errorMessage = e.response.data.detail || e.response.data.message || errorMessage;
      }

      // You might want to dispatch an error action here
      // dispatch({ type: 'SET_ERROR', payload: errorMessage });

      throw new Error(errorMessage);
    }
  };

export const wechatLinkAccount = () => {
  // Generate a cryptographically secure random state for security
  const state = generateRandomState();

  // Store the state in localStorage for validation
  localStorage.setItem('wechat_link_state', state);

  // Construct the WeChat QR code login URL for account linking
  const redirectUri = encodeURIComponent('https://my.soundtalks.cn/app/profile/wechat/callback');
  const wechatLinkUrl = `https://open.weixin.qq.com/connect/qrconnect?appid=${wechatAppId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=${state}#wechat_redirect`;

  // Redirect to WeChat login page for account linking
  window.location.href = wechatLinkUrl;
};

export const wechatLinkCallback =
  (code, state) =>
  async (dispatch, getState, { api }) => {
    dispatch({ type: constants.CALLBACK });

    try {
      // Prepare the request payload
      const payload = {
        code,
        state,
      };

      // Call the backend WeChat OAuth callback endpoint for account linking
      const res = await api.post('/api/v2/wechat/oauth/callback/link-account/', payload);

      dispatch({
        type: constants.CALLBACK_SUCCESS,
        payload: {
          ...res.data,
        },
      });

      // Reload the current user profile to get updated wechat_union_id
      try {
        await dispatch(fetchProfile());
      } catch (profileError) {
        console.warn('Failed to reload user profile after WeChat linking:', profileError);
        // Don't throw here as the linking was successful, just the profile reload failed
      }

      return res.data;
    } catch (e) {
      console.error('WeChat link callback error:', e);

      // Dispatch failure action
      dispatch({ type: constants.CALLBACK_FAILED });

      // Handle different error scenarios
      let errorMessage = 'WeChat account linking failed';
      let errorCode = null;

      if (e.response && e.response.data) {
        const errorData = e.response.data;
        errorMessage = errorData.error || errorData.detail || errorData.message || errorMessage;
        errorCode = errorData.code;
      }

      // Create an error object with both message and code
      const error = new Error(errorMessage);
      error.code = errorCode;
      error.response = e.response;

      throw error;
    }
  };

// WeChat Invitation Token Actions
export const fetchWechatInviteTokens =
  (siteId) =>
  async (dispatch, getState, { api, schema, normalize }) => {
    dispatch({ type: 'WECHAT_INVITE_TOKENS_FETCH_START' });

    try {
      const res = await api.get(`/api/v2/wechat/sites/${siteId}/invite-tokens/list/`);

      // Normalize the data like other actions do
      const payload = {
        items: res.data,
        ...normalize(res.data, [schema.wechatInviteToken]),
      };

      dispatch({
        type: 'WECHAT_INVITE_TOKENS_FETCH_SUCCESS',
        payload,
      });

      return res.data;
    } catch (e) {
      console.error('Failed to fetch WeChat invite tokens:', e);

      dispatch({
        type: 'WECHAT_INVITE_TOKENS_FETCH_FAILED',
        payload: e.response?.data || { message: 'Failed to fetch WeChat invite tokens' },
      });

      throw e;
    }
  };

export const createWechatInviteToken =
  (siteId, description) =>
  async (dispatch, getState, { api, schema, normalize }) => {
    dispatch({ type: 'WECHAT_INVITE_TOKENS_CREATE_START' });

    try {
      const res = await api.post(`/api/v2/wechat/sites/${siteId}/invite-tokens/`, {
        description,
      });

      // Normalize the created token
      const payload = {
        items: [res.data],
        ...normalize([res.data], [schema.wechatInviteToken]),
      };

      dispatch({
        type: 'WECHAT_INVITE_TOKENS_CREATE_SUCCESS',
        payload,
      });

      return res.data;
    } catch (e) {
      console.error('Failed to create WeChat invite token:', e);

      dispatch({
        type: 'WECHAT_INVITE_TOKENS_CREATE_FAILED',
        payload: e.response?.data || { message: 'Failed to create WeChat invite token' },
      });

      throw e;
    }
  };

export const clearWechatInviteTokens = () => ({ type: 'WECHAT_INVITE_TOKENS_CLEAR' });

export const wechatLoginLink =
  (credentials) =>
  async (dispatch, getState, { api }) => {
    dispatch({ type: constants.CALLBACK });

    try {
      // Call the backend WeChat login-link endpoint
      const res = await api.post('/api/v2/wechat/login-link/', credentials);

      if (res.data.access) {
        api.setAuthorization(res.data.access);
        dispatch({
          type: constants.LOGIN_SUCCESS,
          payload: {
            ...res.data,
          },
        });
      }

      dispatch({
        type: constants.CALLBACK_SUCCESS,
        payload: {
          ...res.data,
        },
      });

      return res.data;
    } catch (e) {
      console.error('WeChat login link error:', e);

      // Dispatch failure action
      dispatch({ type: constants.CALLBACK_FAILED });

      // Handle different error scenarios
      let errorMessage = 'WeChat account linking failed';
      if (e.response && e.response.data) {
        errorMessage = e.response.data.detail || e.response.data.message || errorMessage;
      }

      throw new Error(errorMessage);
    }
  };
